import React, { MouseEventHandler, useEffect, useState } from "react";
import TextInput from "../common/TextInput";
import Select from "react-select";
import {
  Country,
  MapMarker,
  Species,
  Video,
  Tag,
  VideoFormatDto,
  VideoStatusDto,
  VideoResolutionDto,
  Organization,
} from "../common/ObjectTypes";
import ManageMapMarkersController from "../mapMarkers/ManageMapMarkersPage";
import SelectInput from "../common/SelectInput";
import { PHOTO_URL } from "../../base";
import { getIconImage } from "../common/ProjectIcons";
import { MoonLoader } from "react-spinners";
import MultiStepForm from "../common/MultiStepForm";
import { getCurrentUser } from "../../helpers/authHeader";
import { getWildXROrgId } from "../../helpers/wildXROrgIdHelper";

type AddEditVideoProps = {
  video: Video;
  countries: Country[];
  species: Species[];
  mapMarkers: MapMarker[];
  tags: Tag[];
  videoFormats: VideoFormatDto[];
  videoStatuses: VideoStatusDto[];
  videoResolutions: VideoResolutionDto[];
  organizations: Organization[];
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeThumbnail: Function;
  onChangeSpecies: Function;
  onSelectMapMarkerForVideo: Function;
  onChangeSingleSelect: Function;
  onChangeTags: Function;
  onChangeTime: Function;
  onSave: MouseEventHandler;
  onCancel: MouseEventHandler;
  existingVideo: boolean;
  errors: any;
  saving: boolean;
  onFileInputChange: Function;
  onUpload: Function;
  uploadStatus: object;
  onUploadVideoPage: Function;
  onActivateVideo: Function;
  changesMade: boolean;
  thumbnailDataUrl: string | null;
  thumbnailName: string | null;
  onDeleteVideo: Function;
  onSelectAllMapMarkersForVideo: Function;
  areAllMapMarkersSelectedForVideo: boolean;
  isLoadingForVideo?: boolean;
};

const AddEditVideo = ({
  video,
  countries,
  species,
  tags,
  videoFormats,
  videoStatuses,
  videoResolutions,
  onChange,
  onChangeThumbnail,
  onChangeSingleSelect,
  onChangeSpecies,
  onSelectMapMarkerForVideo,
  onChangeTags,
  onChangeTime,
  onSave,
  errors,
  saving,
  uploadStatus,
  existingVideo,
  onUploadVideoPage,
  onActivateVideo,
  changesMade,
  thumbnailDataUrl,
  thumbnailName,
  onDeleteVideo,
  organizations,
  onSelectAllMapMarkersForVideo,
  areAllMapMarkersSelectedForVideo,
  isLoadingForVideo,
}: AddEditVideoProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const currentUser = getCurrentUser();
  const wildXROrgId = getWildXROrgId();
  const [readOnly, setReadOnly] = useState(false);
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  //get country by code
  const getCountryByCode = (code: any) => {
    return countries.find((country) => country.id === code);
  };
  const countryId = video.countryObtainedId;
  const defaultCountry = getCountryByCode(countryId);
  //get organization by code
  const getOrganizationByCode = (code: any) => {
    return organizations.find((org) => org.organizationId === code);
  };
  const organizationId = video.organizationId;
  const defaultOrg = getOrganizationByCode(organizationId);
  //get resolution by id
  const getResolutionById = (id: any) => {
    return videoResolutions.find((res) => res.videoResolutionId === id);
  };
  const resolutionID = video.videoResolutionId;
  const defaultResolution = getResolutionById(resolutionID);

  // Get mapMarkers from video object
  // Combine mapMarkers from video object if both are available
  const allMapMarkers = [...(video.mapMarkers || [])];
  // Function to check if a mapMarker is included in any of the mapMarkers
  const isMapMarkerIncluded = (mapMarkerId: string): boolean => {
    return allMapMarkers.some(
      (mapMarker) => mapMarker.mapMarkerId === mapMarkerId
    );
  };

  //get format by id
  const formatId = video.videoFormat;
  const getFormatById = (id: any) => {
    return videoFormats.find((format) => format.videoFormatId === id);
  };
  const defaultFormat = getFormatById(formatId);

  //photo URL
  const photoUrl = PHOTO_URL || "placeholder.jpg";

  //time
  const formatTimeInput = (time: string): string => {
    if (!time) return "__:__:__"; // Return placeholder if time is empty or undefined
    // Remove non-digit characters and format as xx:xx:xx
    const cleaned = time.replace(/\D/g, "");
    return cleaned.replace(/(\d{2})(\d{2})(\d{2})/, "$1:$2:$3");
  };

  //thumbnail
  const thumbnailUrl = thumbnailDataUrl
    ? (thumbnailDataUrl as string)
    : `${photoUrl + video.thumbnailUrl}?t=${new Date().getTime()}`;

  //species
  const defaultSpecies = video.species
    ? video.species?.map((species: any) => ({
        value: species.speciesId,
        label: species.name,
      }))
    : [];

  //tags
  const defaultTags = video.tags
    ? video.tags?.map((tag: any) => ({
        value: tag.tagId,
        label: tag.name,
      }))
    : [];

  //multistep form handling for handling next and back and state persistance
  const [currentStep, setCurrentStep] = useState(0);

  const cannotAccessMultiStep =
    video?.organizationId === wildXROrgId &&
    !currentUser?.isSystemAdmin &&
    !currentUser?.organizations?.some(
      (org: any) => org.organizationId === wildXROrgId
    );

  const steps = [
    { label: existingVideo ? "Edit Video" : "Add Video" },
    { label: "Map Markers" },
    { label: "Review & Save" },
  ];
  const handleNext = () => {
    setCurrentStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
  };
  useEffect(() => {
    if (cannotAccessMultiStep || currentStep === 2) {
      setCurrentStep(steps.length - 1);
      setReadOnly(true);
    } else {
      setReadOnly(false);
    }
  }, [currentStep, cannotAccessMultiStep, steps.length]);

  const handleBack = () => {
    setCurrentStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  const isSystemAdmin = currentUser?.isSystemAdmin || false;
  const hasOrganizationId = !!video?.organizationId;

  // Safely check if the user belongs to the video's organization with null checks
  const isInUserOrganizations =
    currentUser?.organizations && video?.organizationId
      ? currentUser.organizations.some(
          (org: any) => org.organizationId === video.organizationId
        )
      : false;

  const isDisabled =
    readOnly ||
    (existingVideo &&
      !isSystemAdmin &&
      (!hasOrganizationId || !isInUserOrganizations));

  return (
    <div className="w-full my-6">
      {/* Title */}
      <h1 className="text-2xl font-bold text-center text-gray-800 mb-6">
        {existingVideo ? "Video Details" : "Add Video"}
      </h1>
      <div className="w-2/3 mx-auto">
        {/* Container for Stepper and Actions */}
        <div className="flex justify-between items-center mb-6">
          {/* Stepper */}
          {!cannotAccessMultiStep && (
            <div className="flex-grow ml-16">
              <MultiStepForm
                steps={steps}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
              />
            </div>
          )}

          {/* Actions Dropdown */}
          {existingVideo && !cannotAccessMultiStep && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  toggleDropdown();
                }}
                className="px-4 py-2 rounded-lg bg-deepGreen-500 text-white font-medium hover:bg-deepGreen-600 transition"
              >
                Actions
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-white rounded-lg border shadow-lg z-10">
                  <button
                    onClick={(e) => onUploadVideoPage()}
                    className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                  >
                    Upload Video
                  </button>
                  <button
                    onClick={(e) => onDeleteVideo(e, video.videoId)}
                    className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                  >
                    Delete Video
                  </button>
                  {video.videoStatusId === 3 && (
                    <button
                      onClick={(e) => onActivateVideo(e)}
                      className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                    >
                      Activate Video
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Form */}
      <form className="flex flex-col items-center space-y-6">
        {/* Step 0 + Step 2 Form Fields */}
        {(currentStep === 0 || currentStep === 2) && (
          <div className="w-2/3 bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
            {/* Name + Thumbnail */}
            <div className="space-y-4">
              <TextInput
                name="name"
                label="Name*"
                placeholder="Enter Name"
                value={video.name || ""}
                error={errors.name}
                disabled={isDisabled}
                className={"p-2 rounded mb-2 shadow w-full"}
                type="text"
                initialFocus={true}
                onChange={(e: any) => onChange(e)}
                maxLength={162}
              />

              {/* Thumbnail */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Thumbnail*
                </label>
                {errors.thumbnail && (
                  <p className="text-red-500 text-xs">{errors.thumbnail}</p>
                )}
                <div className="space-y-2">
                  {video.thumbnailUrl && (
                    <img
                      id="thumbnail"
                      src={encodeURI(thumbnailUrl)}
                      alt="Thumbnail"
                      className="rounded-lg border object-cover max-h-40"
                      onError={(e) =>
                        console.error("Error loading thumbnail:", e)
                      }
                    />
                  )}
                  <input
                    id="fileInput"
                    type="file"
                    name="file"
                    onChange={(e: any) => onChangeThumbnail(e)}
                    disabled={isDisabled}
                    className="hidden"
                  />
                  <label
                    htmlFor="fileInput"
                    className="cursor-pointer text-tealBlue-500 hover:underline text-sm"
                  >
                    {thumbnailUrl && readOnly ? "" : "Choose a PNG file"}
                  </label>
                </div>
              </div>

              {/* Video File Name */}
              <TextInput
                name={"videoFileName"}
                label={"Video File Name"}
                placeholder={""}
                value={video.videoFileName ?? video.videoId}
                error={errors.overview}
                disabled={true}
                className={"p-2 rounded mb-2 shadow w-full"}
                type={"text"}
                initialFocus={true}
                onChange={() => {}}
              />
            </div>

            {/* Overview, YouTube, Times */}
            <div className="space-y-4">
              <TextInput
                name={"overview"}
                label={"Overview*"}
                placeholder={"Enter Overview"}
                value={video.overview ?? ""}
                error={errors.overview}
                disabled={isDisabled}
                className={"p-2 rounded mb-2 shadow w-full"}
                type={"text"}
                initialFocus={true}
                onChange={(e: any) => onChange(e)}
                rows={15}
                maxLength={1200}
              />

              <TextInput
                name={"youTubeUrl"}
                label={"Youtube URL"}
                placeholder={"Enter Youtube URL"}
                value={video.youTubeUrl ?? ""}
                error={""}
                disabled={isDisabled}
                className={"p-2 rounded mb-2 shadow w-full"}
                type={"text"}
                initialFocus={true}
                onChange={(e: any) => onChange(e)}
              />

              <div className="flex flex-row">
                <TextInput
                  name={"startTime"}
                  label={"Start Time (HH:MM:SS)"}
                  placeholder={"__:__:__"}
                  value={formatTimeInput(video.startTime ?? "")}
                  error={""}
                  maxLength={6}
                  disabled={isDisabled}
                  className={"p-2 rounded shadow w-11/12"}
                  type={"text"}
                  initialFocus={true}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChangeTime("startTime", e.target.value)
                  }
                />
                <TextInput
                  name={"endTime"}
                  label={"End Time (HH:MM:SS)"}
                  placeholder={"__:__:__"}
                  value={formatTimeInput(video.endTime ?? "")}
                  error={""}
                  maxLength={6}
                  disabled={isDisabled}
                  className={"p-2 rounded shadow w-full"}
                  type={"text"}
                  initialFocus={true}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChangeTime("endTime", e.target.value)
                  }
                />
              </div>
            </div>

            {/* Organization, Country, Format, Resolution */}
            <div className="space-y-4">
              <div className="flex flex-col pb-2">
                {" "}
                {currentUser?.isSystemAdmin ||
                currentUser?.organizations?.length > 1 ? (
                  <div className="flex flex-col mb-2 ">
                    {" "}
                    <SelectInput
                      key={defaultOrg ? defaultOrg.organizationId : "default"}
                      name="organizationId"
                      label="Select Organization*"
                      options={organizations.map((org: any) => ({
                        value: org.organizationId,
                        label: org.name,
                      }))}
                      onChange={(e: any) =>
                        onChangeSingleSelect("organizationId", e)
                      }
                      className="w-full"
                      defaultValue={
                        video !== null && defaultOrg
                          ? {
                              value: defaultOrg.organizationId,
                              label: defaultOrg.name,
                            }
                          : undefined
                      }
                      isDisabled={readOnly}
                      isLoading={false}
                      isClearable={true}
                      isSearchable={true}
                      isMulti={false}
                      errors={errors.organizationId}
                    />{" "}
                  </div>
                ) : (
                  <>
                    {" "}
                    {currentUser?.organizations?.map((org: any) => {
                      return (
                        <div className="py-2" key={org.organizationId}>
                          {" "}
                          <span>Organization: </span>{" "}
                          {video.organizationId === wildXROrgId
                            ? "WildXR"
                            : org.name}{" "}
                        </div>
                      );
                    })}{" "}
                  </>
                )}{" "}
              </div>

              <SelectInput
                name="country"
                label="Select Country*"
                options={countries.map((country: any) => ({
                  value: country.id,
                  label: country.name,
                }))}
                onChange={(e: any) =>
                  onChangeSingleSelect("countryObtainedId", e)
                }
                className="w-full"
                defaultValue={{
                  value: defaultCountry?.id,
                  label: defaultCountry?.name || "",
                }}
                isDisabled={isDisabled}
                isLoading={false}
                isClearable={true}
                isSearchable={true}
                isMulti={false}
                errors={errors.country}
              />
              <SelectInput
                name="videoFormat"
                label="Select Video Format*"
                options={videoFormats.map((format: any) => ({
                  value: format.videoFormatId,
                  label: format.name,
                }))}
                onChange={(e: any) => onChangeSingleSelect("videoFormat", e)}
                className="w-full"
                defaultValue={
                  defaultFormat
                    ? {
                        value: defaultFormat?.videoFormatId,
                        label: defaultFormat?.name,
                      }
                    : undefined
                }
                isDisabled={isDisabled}
                isLoading={false}
                isClearable={true}
                isSearchable={true}
                isMulti={false}
                errors={errors.videoFormat}
              />
              <SelectInput
                name="videoResolution"
                label="Select Video Resolution*"
                options={videoResolutions.map((resolution: any) => ({
                  value: resolution.videoResolutionId,
                  label: resolution.name,
                }))}
                onChange={(e: any) =>
                  onChangeSingleSelect("videoResolutionId", e)
                }
                className="w-full"
                defaultValue={{
                  value: defaultResolution?.videoResolutionId,
                  label: defaultResolution?.name || "",
                }}
                isDisabled={isDisabled}
                isLoading={false}
                isClearable={true}
                isSearchable={true}
                isMulti={false}
                errors={errors.resolution}
              />
            </div>

            {/* Species & Tags */}
            <div className="space-y-4">
              <SelectInput
                name="species"
                label="Select Species*"
                options={species.map((species) => ({
                  value: species.speciesId,
                  label: species.name,
                }))}
                onChange={(selectedOptions: any) =>
                  onChangeSpecies(selectedOptions)
                }
                className="w-full"
                defaultValue={defaultSpecies || []}
                isDisabled={isDisabled}
                isLoading={false}
                isClearable={true}
                isSearchable={true}
                isMulti={true}
                errors={errors.species}
              />{" "}
              <SelectInput
                name="tags"
                label="Select Tags"
                options={tags.map((tag) => ({
                  value: tag.tagId,
                  label: tag.name,
                }))}
                onChange={(selectedOptions: any) =>
                  onChangeTags(selectedOptions)
                }
                className="w-full mb-2"
                defaultValue={defaultTags || []}
                isDisabled={isDisabled}
                isLoading={false}
                isClearable={true}
                isSearchable={true}
                isMulti={true}
                errors={errors.tags}
              />{" "}
            </div>

            {/* Map Markers (Read Only) */}
            {readOnly && (
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-gray-800">
                  Map Markers
                </h2>
                <div className="bg-gray-50 border rounded-lg p-3 space-y-2">
                  {video.mapMarkers?.map((m: any) => (
                    <div
                      key={m.mapMarkerId}
                      className="flex items-center space-x-2"
                    >
                      {getIconImage(m.iconID.toString())}
                      <p>{m.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Manage Map Markers */}
        {currentStep === 1 && (
          <div className="w-2/3 bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
            <ManageMapMarkersController
              selectMapMarkersPage={true}
              onSelectMapMarkerForVideo={onSelectMapMarkerForVideo}
              isVideoMapMarkerIncluded={isMapMarkerIncluded}
              onSelectAllMapMarkersForVideo={onSelectAllMapMarkersForVideo}
              areAllMapMarkersSelected={areAllMapMarkersSelectedForVideo}
              isLoadingForVideo={isLoadingForVideo}
              isDisabled={isDisabled}
            />
          </div>
        )}
      </form>

      {/* Navigation Buttons */}
      <div className={cannotAccessMultiStep ? "hidden" : "visible"}>
        <div className="flex justify-center mt-6">
          <div className="w-2/3 flex justify-between">
            <button
              className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 transition disabled:opacity-50"
              onClick={handleBack}
              disabled={currentStep === 0}
            >
              Back
            </button>
            {currentStep < steps.length - 1 ? (
              <button
                className="px-4 py-2 rounded-lg bg-deepGreen-500 text-white font-medium hover:bg-deepGreen-600 transition"
                onClick={handleNext}
              >
                Next
              </button>
            ) : saving ? (
              <div className="flex items-center justify-center">
                <MoonLoader color="#9EB45B" />
              </div>
            ) : (
              <button
                onClick={onSave}
                disabled={!changesMade}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  !changesMade
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-deepGreen-500 text-white hover:bg-deepGreen-600"
                }`}
              >
                Save
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditVideo;
